// Hook: Market data via REST API polling (with WebSocket as bonus if available)
import { useEffect, useCallback, useState, useRef } from 'react';
import { useTrading } from '../context/TradingContext';
import { KEY_INDICES } from '../config/angelOne';
import angelOneApi from '../services/angelOneApi';

// Fetch full quotes for multiple tokens via REST API
async function fetchMarketQuotes(mode, exchangeTokenMap) {
  try {
    const result = await angelOneApi.getQuote({ mode, exchangeTokens: exchangeTokenMap });
    return result.data;
  } catch (e) {
    console.warn('Market quote fetch failed:', e);
    return null;
  }
}

export function useMarketData(tokens = [], mode = 'LTP') {
  const { getQuote, connectionStatus } = useTrading();
  const [restData, setRestData] = useState({});
  const intervalRef = useRef(null);

  useEffect(() => {
    if (connectionStatus !== 'connected' || tokens.length === 0) return;

    const fetchData = async () => {
      // Group tokens by exchange
      const exchangeMap = {};
      tokens.forEach(t => {
        if (!exchangeMap[t.exchange]) exchangeMap[t.exchange] = [];
        exchangeMap[t.exchange].push(String(t.token));
      });

      const data = await fetchMarketQuotes(mode === 'LTP' ? 'LTP' : 'FULL', exchangeMap);
      if (data?.data?.fetched) {
        const newData = {};
        data.data.fetched.forEach(item => {
          newData[`${item.exchange}_${item.symbolToken || item.symboltoken}`] = item;
        });
        setRestData(newData);
      }
    };

    fetchData();
    intervalRef.current = setInterval(fetchData, 8000);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [connectionStatus, tokens, mode]);

  const getPrice = useCallback((exchange, token) => {
    const wsData = getQuote(exchange, token);
    const restItem = restData[`${exchange}_${token}`];
    return wsData?.ltp ? wsData : restItem || null;
  }, [getQuote, restData]);

  return { getPrice, data: restData };
}

export function useIndexData() {
  const { connectionStatus } = useTrading();
  const [indexQuotes, setIndexQuotes] = useState({});
  const intervalRef = useRef(null);

  useEffect(() => {
    if (connectionStatus !== 'connected') return;

    const fetchIndices = async () => {
      // Group indices by exchange
      const exchangeMap = {};
      KEY_INDICES.forEach(idx => {
        if (!exchangeMap[idx.exchange]) exchangeMap[idx.exchange] = [];
        exchangeMap[idx.exchange].push(String(idx.token));
      });

      try {
        const result = await angelOneApi.getQuote({
          mode: 'FULL',
          exchangeTokens: exchangeMap,
        });

        const apiData = result.data;

        // Handle different response formats
        if (apiData?.data?.fetched) {
          const newQuotes = {};
          apiData.data.fetched.forEach(item => {
            const key = `${item.exchange}_${item.symbolToken || item.symboltoken}`;
            newQuotes[key] = {
              ltp: parseFloat(item.ltp) || 0,
              open: parseFloat(item.open) || 0,
              high: parseFloat(item.high) || 0,
              low: parseFloat(item.low) || 0,
              close: parseFloat(item.close) || 0,
              volume: parseInt(item.volume) || 0,
              change: parseFloat(item.netChange) || 0,
              changePct: parseFloat(item.percentChange) || 0,
            };
          });
          setIndexQuotes(newQuotes);
        } else if (apiData?.data) {
          // Alternate format: data might be keyed by exchange
          const newQuotes = {};
          const data = apiData.data;
          // Could be { NSE: [{...}], BSE: [{...}] }
          Object.entries(data).forEach(([exchange, items]) => {
            if (Array.isArray(items)) {
              items.forEach(item => {
                const key = `${exchange}_${item.symbolToken || item.symboltoken || item.token}`;
                newQuotes[key] = {
                  ltp: parseFloat(item.ltp) || 0,
                  open: parseFloat(item.open) || 0,
                  high: parseFloat(item.high) || 0,
                  low: parseFloat(item.low) || 0,
                  close: parseFloat(item.close) || 0,
                  volume: parseInt(item.tradeVolume || item.volume) || 0,
                  change: parseFloat(item.netChange) || 0,
                  changePct: parseFloat(item.percentChange) || 0,
                };
              });
            }
          });
          if (Object.keys(newQuotes).length > 0) {
            setIndexQuotes(newQuotes);
          }
        }
      } catch (e) {
        console.warn('Index data fetch failed:', e);
      }
    };

    fetchIndices();
    intervalRef.current = setInterval(fetchIndices, 5000);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [connectionStatus]);

  const indices = KEY_INDICES.map(idx => ({
    ...idx,
    data: indexQuotes[`${idx.exchange}_${idx.token}`] || null,
  }));

  return indices;
}

export function useLTP(exchange, token) {
  const { connectionStatus } = useTrading();
  const [data, setData] = useState(null);
  const intervalRef = useRef(null);

  useEffect(() => {
    if (connectionStatus !== 'connected' || !token) return;

    const fetchLTP = async () => {
      try {
        const result = await angelOneApi.getLTP({
          exchange,
          tradingsymbol: '',
          symboltoken: String(token),
        });
        if (result.data?.data) {
          setData(result.data.data);
        }
      } catch (e) {
        // Silent
      }
    };

    fetchLTP();
    intervalRef.current = setInterval(fetchLTP, 8000);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [connectionStatus, exchange, token]);

  return data;
}

export function useCandleData(exchange, token, interval, fromDate, toDate) {
  const [candles, setCandles] = useState([]);
  const [loading, setLoading] = useState(false);
  const { connectionStatus } = useTrading();

  const fetchCandles = useCallback(async () => {
    if (connectionStatus !== 'connected' || !token) return;
    setLoading(true);
    try {
      const result = await angelOneApi.getCandles({
        exchange,
        symboltoken: String(token),
        interval,
        fromdate: fromDate,
        todate: toDate,
      });
      if (result.data?.data) {
        setCandles(result.data.data.map(c => ({
          time: c[0],
          open: c[1],
          high: c[2],
          low: c[3],
          close: c[4],
          volume: c[5],
        })));
      }
    } catch (e) {
      console.error('Candle fetch failed:', e);
    } finally {
      setLoading(false);
    }
  }, [connectionStatus, exchange, token, interval, fromDate, toDate]);

  useEffect(() => {
    fetchCandles();
  }, [fetchCandles]);

  return { candles, loading, refetch: fetchCandles };
}
